import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Cookie, Layers, ShieldCheck, ExternalLink, Sliders, History,
  ChevronRight, ArrowRight, CheckCircle2, Lock, Eye, Sparkles
} from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import SEO from "@/components/SEO";

const SECTIONS = [
  { id: "what-are-cookies", title: "1. What Are Cookies?", icon: Cookie },
  { id: "types-of-cookies", title: "2. Types of Cookies We May Use", icon: Layers },
  { id: "client-portal", title: "3. Cookies & Client Portal", icon: ShieldCheck },
  { id: "third-party", title: "4. Third-Party Cookies", icon: ExternalLink },
  { id: "managing-cookies", title: "5. Managing Cookies", icon: Sliders },
  { id: "changes", title: "6. Changes", icon: History },
];

const COOKIE_TYPES = [
  {
    name: "Essential Cookies",
    desc: "Required for basic website functionality, security, login and navigation.",
    badge: "Strictly Necessary",
    color: "emerald",
    icon: Lock,
  },
  {
    name: "Functional Cookies",
    desc: "Used to remember user preferences and improve functionality.",
    badge: "Preference & UX",
    color: "blue",
    icon: Sliders,
  },
  {
    name: "Analytics Cookies",
    desc: "Used to understand how visitors use the website and identify areas for improvement.",
    badge: "Performance",
    color: "gold",
    icon: Eye,
  },
  {
    name: "Marketing Cookies",
    desc: "Where used, these may help measure advertising or marketing activity.",
    badge: "Campaign Analytics",
    color: "purple",
    icon: Sparkles,
  },
];

export default function CookiePolicy() {
  const [activeSection, setActiveSection] = useState("what-are-cookies");

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 140;
      for (const section of SECTIONS) {
        const el = document.getElementById(section.id);
        if (el) {
          const top = el.offsetTop;
          const height = el.offsetHeight;
          if (scrollPosition >= top && scrollPosition < top + height) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const navOffset = 100;
      const top = el.getBoundingClientRect().top + window.pageYOffset - navOffset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-navy-900 antialiased" data-testid="cookie-policy-page">
      <SEO
        title="Cookie Policy | Biz360 Compliance"
        description="Official Cookie Policy for Biz360 Compliance explaining our use of essential, functional, analytics, and third-party cookies on our digital platform."
        canonical="/cookie-policy"
        breadcrumbs={[{ label: "Cookie Policy", href: "/cookie-policy" }]}
      />
      <ScrollProgress />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-200">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-5xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="cookie-breadcrumb">
                <Link to="/" className="hover:text-navy-800 transition-colors">Home</Link>
                <ChevronRight className="h-3 w-3" />
                <span className="text-navy-700">Cookie Policy</span>
              </div>
            </Reveal>

            <div className="text-center">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full border border-gold-300/60 bg-gold-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-gold-700">
                  Tracking & Transparency
                </span>
              </Reveal>

              <Reveal delay={0.1}>
                <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-navy-900 md:text-5xl">
                  COOKIE POLICY
                </h1>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-white/80 px-4 py-1.5 text-xs font-medium text-slate-600 shadow-sm border border-slate-200">
                  <History className="h-3.5 w-3.5 text-gold-600" />
                  <span>Last Updated: <strong className="text-navy-900">01/09/2026</strong></span>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-navy-900/10 bg-white/90 p-6 text-left shadow-sm backdrop-blur-sm">
                  <p className="text-base font-semibold text-navy-900">
                    Biz360 Compliance may use cookies and similar technologies on its website and digital platforms.
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    This Cookie Policy explains what cookies are, how they may be used and the choices available to users.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* Content Container */}
        <section className="mx-auto max-w-7xl px-5 py-12 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            
            {/* Quick Navigation Sticky Sidebar */}
            <aside className="hidden lg:col-span-4 lg:block">
              <div className="sticky top-28 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm max-h-[calc(100vh-140px)] overflow-y-auto">
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-3 px-2">
                  Table of Contents
                </h3>
                <nav className="space-y-1">
                  {SECTIONS.map((sec) => {
                    const SIcon = sec.icon;
                    const isActive = activeSection === sec.id;
                    return (
                      <button
                        key={sec.id}
                        onClick={() => scrollToSection(sec.id)}
                        className={`flex w-full items-center gap-2.5 rounded-xl px-3 py-2 text-left text-xs font-medium transition-all ${
                          isActive
                            ? "bg-navy-900 text-white shadow-sm"
                            : "text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                        }`}
                      >
                        <SIcon className={`h-4 w-4 shrink-0 ${isActive ? "text-gold-400" : "text-slate-400"}`} />
                        <span className="truncate">{sec.title}</span>
                      </button>
                    );
                  })}
                </nav>

                <div className="mt-6 border-t border-slate-100 pt-5">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 px-2">
                    Related Legal Policies
                  </h4>
                  <div className="space-y-1.5 text-xs font-medium">
                    <Link
                      to="/terms"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Terms & Conditions</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/privacy-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Privacy Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-security-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Security Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/data-retention-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Data Retention Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                    <Link
                      to="/refund-policy"
                      className="flex items-center justify-between rounded-lg px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-navy-900"
                    >
                      <span>Refund & Cancellation Policy</span>
                      <ArrowRight className="h-3 w-3 text-slate-400" />
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Policy Content */}
            <div className="space-y-8 lg:col-span-8">

              {/* 1. What Are Cookies? */}
              <article id="what-are-cookies" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Cookie className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">1. What Are Cookies?</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Cookies are small text files or similar technologies stored on or associated with a user&apos;s device when visiting a website.
                </p>
                <p className="mt-3 text-sm font-semibold text-navy-900">They may help a website:</p>
                <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Function properly;",
                    "Remember preferences;",
                    "Maintain secure sessions;",
                    "Understand website usage; and",
                    "Improve user experience."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-lg bg-slate-50 p-3 border border-slate-100">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 2. Types of Cookies We May Use */}
              <article id="types-of-cookies" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Layers className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">2. Types of Cookies We May Use</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Depending on your interaction with our website and portal, we may use the following categories:
                </p>

                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {COOKIE_TYPES.map((c) => {
                    const CIcon = c.icon;
                    return (
                      <div key={c.name} className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 transition-all hover:bg-white hover:shadow-sm">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="grid h-8 w-8 place-items-center rounded-lg bg-navy-900 text-gold-400">
                              <CIcon className="h-4 w-4" />
                            </span>
                            <h3 className="text-sm font-bold text-navy-900">{c.name}</h3>
                          </div>
                          <span className="rounded-full bg-slate-200/70 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-700">
                            {c.badge}
                          </span>
                        </div>
                        <p className="mt-3 text-xs leading-relaxed text-slate-600">
                          {c.desc}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </article>

              {/* 3. Cookies & Client Portal */}
              <article id="client-portal" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ShieldCheck className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">3. Cookies & Client Portal</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Cookies or similar technologies may be used to maintain:
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 text-sm text-slate-700">
                  {[
                    "Login sessions;",
                    "Authentication;",
                    "Security controls;",
                    "User preferences; and",
                    "Portal functionality."
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2.5 rounded-lg bg-slate-50 p-3 border border-slate-100">
                      <span className="h-2 w-2 rounded-full bg-gold-500 shrink-0" />
                      <span className="text-xs font-semibold text-slate-800">{item}</span>
                    </div>
                  ))}
                </div>
              </article>

              {/* 4. Third-Party Cookies */}
              <article id="third-party" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <ExternalLink className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">4. Third-Party Cookies</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  Some services integrated into the website may place their own cookies or similar technologies.
                </p>
                <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-700 leading-relaxed">
                  These may include analytics, payment, communication or other service providers. Their use is governed by their respective policies.
                </div>
              </article>

              {/* 5. Managing Cookies */}
              <article id="managing-cookies" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <Sliders className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">5. Managing Cookies</h2>
                </div>
                <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-700">
                  <p>
                    You may be able to manage or block cookies through your browser settings.
                  </p>
                  <p className="rounded-xl border border-amber-200 bg-amber-50/60 p-3.5 text-xs font-semibold text-amber-950">
                    Notice: Disabling certain cookies may affect website or portal functionality.
                  </p>
                  <p>
                    Where consent is legally required for non-essential cookies, appropriate consent mechanisms may be provided.
                  </p>
                </div>
              </article>

              {/* 6. Changes */}
              <article id="changes" className="rounded-2xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm transition-all hover:border-slate-300">
                <div className="flex items-center gap-3">
                  <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-900 text-gold-400">
                    <History className="h-5 w-5" />
                  </span>
                  <h2 className="text-xl font-bold text-navy-900">6. Changes</h2>
                </div>
                <p className="mt-4 text-sm leading-relaxed text-slate-700">
                  This Cookie Policy may be updated when our website technologies or legal requirements change.
                </p>
              </article>

            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFab />
    </div>
  );
}
